import React, { useState, useEffect, useRef } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { ToastContainer, toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import styles from './AdminFirmCreate.module.css';
import { firmApi } from '../../services/firmApi';
import { Country, FirmFormData } from '../../types/firm';

const AdminFirmCreate: React.FC = () => {
  const [formData, setFormData] = useState<FirmFormData>({
    name: '',
    abbrev: '',
    url: '',
    domain: '',
    Country_ID: null,
    logo: null,
    status: 1
  });
  const [countries, setCountries] = useState<Country[]>([]);
  const [imagePreview, setImagePreview] = useState<string | null>(null);
  const navigate = useNavigate();
  const fileInputRef = useRef<HTMLInputElement>(null);
  const countriesLoadedRef = useRef<boolean>(false);

  useEffect(() => {
    // Get countries on first load
    fetchCountries();
  }, []);

  const fetchCountries = async () => {
    try {
      console.log('Fetching countries for firm management...');
      const countriesData = await firmApi.getCountriesLookupForFirms();
      console.log('Original countries from API:', countriesData);
      
  // Normalize country data to ensure Country_ID is always a string
      const normalizedCountries = countriesData.map((country: any) => ({
        ...country,
        Country_ID: String(country.Country_ID)
      }));
      
      console.log('Normalized countries:', normalizedCountries);
      setCountries(normalizedCountries);
      countriesLoadedRef.current = true;
      
      return normalizedCountries;
    } catch (error) {
      console.error('Error fetching countries:', error);
      // Display backend error detail if available
      const errorMessage = error instanceof Error ? error.message : 'Failed to load countries data';
      toast.error(errorMessage, {
        position: "top-right",
        autoClose: 5000
      });
      return [];
    }
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    const { id, value } = e.target;
    
    // Map input IDs to field names
    const fieldNameMap: { [key: string]: string } = {
      'inputName': 'name',
      'inputAbbrev': 'abbrev',
      'inputUrl': 'url',
      'inputCountry_ID': 'Country_ID',
      'inputDomain': 'domain'
    };
    
    const fieldName = fieldNameMap[id];
    
    if (!fieldName) {
      console.warn(`Unknown input ID: ${id}`);
      return;
    }
    
    console.log(`Input changed: ${id} -> fieldName: ${fieldName}, value: ${value}`);
    
    // Use functional update to ensure we're working with the latest state
    setFormData(prevFormData => {
      // Create a deep copy of the current state
      const updatedFormData = { ...prevFormData };
      
      // Special handling for Country_ID field
      if (fieldName === 'Country_ID') {
        if (value === '') {
          updatedFormData.Country_ID = null;
          console.log('Setting Country_ID to null (empty selection)');
        } else {
          updatedFormData.Country_ID = value;
          console.log('Setting Country_ID to string:', value);
        }
      } else {
        // For other fields, set the value directly
        (updatedFormData as any)[fieldName] = value;
        console.log(`Updated ${fieldName} to:`, value);
      }
      
      console.log('New form data:', updatedFormData);
      return updatedFormData;
    });
  };

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      // Check file size (5MB limit)
      if (file.size > 5 * 1024 * 1024) {
        toast.error('File size exceeds 5MB limit', {
          position: "top-right",
          autoClose: 3000
        });
        return;
      }

      // Check file type
      if (!['image/jpeg', 'image/png'].includes(file.type)) {
        toast.error('Only JPG and PNG files are allowed', {
          position: "top-right",
          autoClose: 3000
        });
        return;
      }

      // Read file as base64
      const reader = new FileReader();
      reader.onload = (event) => {
        const base64String = event.target?.result as string;
        // Extract only the base64 data part
        const base64Data = base64String.split(',')[1];
        setImagePreview(base64String);
        
        // Create a new object to avoid state update conflicts
        const newFormData = { ...formData, logo: base64Data };
        setFormData(newFormData);
      };
      reader.readAsDataURL(file);
    }
  };

  const handleUploadClick = () => {
    fileInputRef.current?.click();
  };

  const validateForm = () => {
    // Check required fields - all fields except logo are required
    if (!formData.name || formData.name.trim() === '') {
      toast.error('Firm name is required', {
        position: "top-right",
        autoClose: 3000
      });
      return false;
    }

    if (!formData.abbrev || formData.abbrev.trim() === '') {
      toast.error('Firm abbreviation is required', {
        position: "top-right",
        autoClose: 3000
      });
      return false;
    }

    if (!formData.url || formData.url.trim() === '') {
      toast.error('Firm URL is required', {
        position: "top-right",
        autoClose: 3000
      });
      return false;
    }

    if (!formData.domain || formData.domain.trim() === '') {
      toast.error('Mail domain is required', {
        position: "top-right",
        autoClose: 3000
      });
      return false;
    }

    if (formData.Country_ID === null) {
      toast.error('Country is required', {
        position: "top-right",
        autoClose: 3000
      });
      return false;
    }

    return true;
  };

  const handleCreate = async () => {
    try {
      if (!validateForm()) {
        return;
      }

      // Convert form data to API format
      const dataToSend = {
        name: formData.name,
        abbrev: formData.abbrev,
        Country_ID: formData.Country_ID,
        logo: formData.logo,
        url: formData.url || null,
        primary_color: formData.primary_color || null,
        secondary_color: formData.secondary_color || null,
        domain: formData.domain || null,
        status: formData.status
      };
      
      // Handle Country_ID field correctly
      console.log('Country_ID before save preparation:', dataToSend.Country_ID, 'Type:', typeof dataToSend.Country_ID);
      
      // If it's undefined or null, explicitly set it to null
      if (dataToSend.Country_ID === undefined || dataToSend.Country_ID === null || dataToSend.Country_ID === '') {
        dataToSend.Country_ID = null;
        console.log('Setting Country_ID to null');
      } else {
        dataToSend.Country_ID = dataToSend.Country_ID;
        console.log('Using Country_ID value:', dataToSend.Country_ID);
      }
      
      console.log('Sending data to API:', dataToSend);
      
      // Create the firm
      const newFirm = await firmApi.createFirm(dataToSend);
      console.log('Response from API after creation:', newFirm);
      
      toast.success('Firm created successfully', {
        position: "top-right",
        autoClose: 3000
      });

      // Navigate to the firm detail page
      setTimeout(() => {
        navigate(`/admin/firms/${newFirm.Firm_ID}`);
      }, 1000);
      
    } catch (error) {
      console.error('Error creating firm:', error);
      toast.error('Failed to create firm', {
        position: "top-right",
        autoClose: 3000
      });
    }
  };

  return (
    <div className={styles.root}>
      <div className="container mt-4">
        <ToastContainer />

        {/* Breadcrumb */}
        <nav aria-label="breadcrumb">
          <ol className={`breadcrumb ${styles.breadcrumb}`}>
            <li className={`breadcrumb-item ${styles.breadcrumbItem}`}>
              <Link to="/admin"><i className="bi bi-house-door me-1"></i>Admin</Link>
            </li>
            <li className={`breadcrumb-item ${styles.breadcrumbItem}`}>
              <Link to="/admin/firms">Firm Management</Link>
            </li>
            <li className={`breadcrumb-item active ${styles.breadcrumbItem}`} aria-current="page">
              Create New Firm
            </li>
          </ol>
        </nav>
        
        <div className="d-flex justify-content-between align-items-center mb-3">
          <h3 className="mb-0">Create New Firm</h3>
        </div>

        <div className="row">
          {/* Firm Logo Card */}
          <div className="col-xl-4">
            <div className={`card mb-4 mb-xl-0 ${styles.userFormCard}`}>
              <div className={`card-header ${styles.cardHeader}`}>Firm Picture</div>
              <div className={`card-body text-center ${styles.cardBody}`}>
                <div className={styles.profilePictureContainer}>
                  {imagePreview ? (
                    <img
                      src={imagePreview}
                      alt="Firm Logo"
                      className={styles.profileImage}
                    />
                  ) : (
                    <div
                      className={styles.profileImage}
                      style={{ backgroundColor: '#e9ecef' }}
                    ></div>
                  )}
                  <div className={styles.profileImageHelpText}>
                    JPG or PNG no larger than 5 MB
                  </div>
                  <input
                    type="file"
                    id="fileInput"
                    ref={fileInputRef}
                    accept="image/png, image/jpeg"
                    className="d-none"
                    onChange={handleFileChange}
                  />
                  <button 
                    id="uploadButton" 
                    className="btn btn-primary" 
                    type="button"
                    onClick={handleUploadClick}
                  >
                    Upload new image
                  </button>
                </div>
              </div>
            </div>
          </div>

          {/* Firm Details Card */}
          <div className="col-xl-8">
            <div className={`card mb-4 ${styles.userFormCard}`}>
              <div className={`card-header ${styles.cardHeader}`}>Firm Details</div>
              <div className={`card-body ${styles.cardBody}`}>
                <form>
                  {/* Form Row - Name */}
                  <div className={styles.formGroup}>
                    <label className={styles.formLabel} htmlFor="inputName">
                      Name
                    </label>
                    <input
                      className={`${styles.formControl} ${
                        !formData.name ? styles.isInvalid : ''
                      }`}
                      id="inputName"
                      type="text"
                      placeholder="Enter firm name"
                      value={formData.name}
                      onChange={handleInputChange}
                    />
                  </div>

                  {/* Form Row - Abbrev */}
                  <div className={styles.formGroup}>
                    <label className={styles.formLabel} htmlFor="inputAbbrev">
                      Abbrev
                    </label>
                    <input
                      className={`${styles.formControl} ${
                        !formData.abbrev ? styles.isInvalid : ''
                      }`}
                      id="inputAbbrev"
                      type="text"
                      placeholder="Enter firm abbreviation"
                      value={formData.abbrev}
                      onChange={handleInputChange}
                    />
                  </div>

                  {/* Form Row - URL */}
                  <div className={styles.formGroup}>
                    <label className={styles.formLabel} htmlFor="inputUrl">
                      URL
                    </label>
                    <input
                      className={`${styles.formControl} ${
                        !formData.url ? styles.isInvalid : ''
                      }`}
                      id="inputUrl"
                      type="url"
                      placeholder="Enter firm URL"
                      value={formData.url}
                      onChange={handleInputChange}
                    />
                  </div>

                  {/* Form Row - Country */}
                  <div className={styles.formGroup}>
                    <label className={styles.formLabel} htmlFor="inputCountry_ID">
                      Country
                    </label>
                    <select
                      className={`${styles.formControl} ${
                        formData.Country_ID === null ? styles.isInvalid : ''
                      }`}
                      id="inputCountry_ID"
                      value={formData.Country_ID !== undefined && formData.Country_ID !== null ? String(formData.Country_ID) : ''}
                      onChange={handleInputChange}
                    >
                      <option value="">Select Country</option>
                      {countries.map((country) => (
                        <option 
                          key={country.Country_ID} 
                          value={String(country.Country_ID)}
                        >
                          {country.name}
                        </option>
                      ))}
                    </select>
                  </div>

                  {/* Form Row - Domain */}
                  <div className={styles.formGroup}>
                    <label className={styles.formLabel} htmlFor="inputDomain">
                      Mail Domain link @*****.*** without @
                    </label>
                    <input
                      className={`${styles.formControl} ${
                        !formData.domain ? styles.isInvalid : ''
                      }`}
                      id="inputDomain"
                      type="text"
                      placeholder="Enter mail domain (without @)"
                      value={formData.domain}
                      onChange={handleInputChange}
                    />
                  </div>

                  {/* Create Button */}
                  <div className={styles.formButtons}>
                    <button 
                      className={`btn btn-primary me-md-2 ${styles.btnPrimary}`} 
                      type="button" 
                      onClick={handleCreate}
                    >
                      Create Firm
                    </button>
                  </div>
                </form>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>

  );
};

export default AdminFirmCreate;
